import React, {useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import clinicService from '../../../../../services/clinic.service';
import {toast} from 'react-toastify';
import {useForm} from 'react-hook-form';
import Loading from "../../../../common/Loading";
import Swal from 'sweetalert2';

const LensTab = () => {
    const [loading, setLoading] = useState(true);
    const [lensList, setLensList] = useState([]);
    const [showAddForm, setShowAddForm] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const {clinicId} = useParams();

    const {
        register,
        handleSubmit,
        formState: {errors, isSubmitting},
        reset
    } = useForm();

    useEffect(() => {
        fetchLensList();
    }, [clinicId]);

    const fetchLensList = () => {
        setLoading(true);
        clinicService.getLens(clinicId)
            .then(res => {
                setLensList(res.data);
                setLoading(false);
            })
            .catch(err => {
                setLoading(false);
            });
    };

    const handleAddSubmit = (data) => {
        clinicService.addUpdateLens(clinicId, data)
            .then(res => {
                fetchLensList();
                setShowAddForm(false);
                reset();
            })
            .catch(err => {
                toast.error("Failed to add brand");
            });
    };

    const handleStatusChange = (lensId, newStatus, brandName) => {
        clinicService.addUpdateLens(clinicId, {
            id: lensId,
            isActive: newStatus,
            clinicId: clinicId,
            brandName: brandName
        })
            .then(() => {
                fetchLensList();
            })
            .catch(err => {
                toast.error("Failed to update status");
            });
    };

    const handleDelete = (lensId, brandName) => {
        Swal.fire({
            title: 'Delete Brand?',
            html: `Are you sure you want to delete <strong>${brandName}</strong>?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: `
            <i class="bi bi-check"></i>
            Yes, delete it!`,
            cancelButtonText: `
            <i class="bi bi-x"></i> 
            Cancel`,
            reverseButtons: true,
            focusCancel: true,
            customClass: {
                confirmButton: `btn btn-success btn-sm me-2`,
                cancelButton: 'btn btn-secondary btn-sm',
                title: `card-title`,
                html: `small`
            }
        }).then((result) => {
            if (result.isConfirmed) {
                // Optimistically update the UI
                const updatedList = lensList.filter(item => item.id !== lensId);
                setLensList(updatedList);

                clinicService.deleteLens(clinicId, lensId)
                    .then(() => {
                        Swal.fire(
                            'Deleted!',
                            'The brand has been deleted.',
                            'success'
                        );
                        // If we deleted the last item, the empty state will automatically show
                    })
                    .catch(err => {
                        // Revert if deletion fails
                        fetchLensList();
                        Swal.fire(
                            'Error!',
                            'Failed to delete the brand.',
                            'error'
                        );
                    });
            }
        });
    };

    return (
        <div className="container-fluid p-0">
            <div className="card-padding shadow-none">
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <h4 className="card-title mb-0">
                            <i className="bi bi-eye-fill me-2"></i>
                            Contact Lens Brands
                        </h4>
                        <p className="text-muted mb-0">Manage Contact Lens Brands</p>
                    </div>
                    <div className="d-flex gap-2">
                        <button
                            className={`btn ${showAddForm ? 'btn-outline-danger' : 'btn-outline-primary'} btn-sm`}
                            onClick={() => setShowAddForm(!showAddForm)}
                        >
                            <i className={`bi ${showAddForm ? 'bi-x' : 'bi-plus-lg'} me-1`}></i>
                            {showAddForm ? 'Cancel' : 'Add Brand'}
                        </button>
                        <button
                            className={`btn btn-sm ${editMode ? 'btn-outline-danger' : 'btn-outline-primary'}`}
                            onClick={() => setEditMode(!editMode)}
                        >
                            <i className={`bi ${editMode ? 'bi-x' : 'bi-pencil-square'} me-1`}></i>
                            {editMode ? 'Cancel' : 'Edit'}
                        </button>
                    </div>
                </div>
            </div>

            {showAddForm && (
                <div className="card-padding">
                    <div className="card card-border card-padding mb-4 shadow-sm">
                        <div className="card-title">Add New Lens Brand</div>
                        <div className="card-body">
                            <form onSubmit={handleSubmit(handleAddSubmit)}>
                                <div className="row g-3 align-items-end">
                                    <div className="col-md-2">
                                        <label className="form-label">Status</label>
                                        <select
                                            className="form-select form-select-sm"
                                            {...register("isActive", {required: true})}
                                        >
                                            <option value="true">Active</option>
                                            <option value="false">Inactive</option>
                                        </select>
                                    </div>
                                    <div className="col-md-8">
                                        <label className="form-label">Brand Name</label>
                                        <input
                                            type="text"
                                            className={`form-control form-control-sm ${errors.brandName ? 'is-invalid' : ''}`}
                                            placeholder="Enter brand name"
                                            {...register("brandName", {required: "Brand name is required"})}
                                        />
                                        {errors.brandName && (
                                            <div className="invalid-feedback">
                                                {errors.brandName.message}
                                            </div>
                                        )}
                                    </div>
                                    <div className="col-md-2">
                                        <button
                                            type="submit"
                                            className="btn btn-success btn-sm w-100"
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? (
                                                <span className="spinner-border spinner-border-sm me-1"></span>
                                            ) : (
                                                <i className="bi bi-floppy me-1"></i>
                                            )}
                                            Save
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {loading ? (
                <Loading/>
            ) : lensList.length === 0 ? (
                <div className="card shadow-none card-padding">
                    <div className="card-body text-center py-5">
                        <i className="bi bi-eye-slash fs-1 text-muted mb-3"></i>
                        <h5>No Lens Brands Found</h5>
                        <p className="text-muted mb-3">
                            Add your first contact lens brand to get started
                        </p>
                    </div>
                </div>
            ) : (
                <div className="card-padding">
                    <div className="card card-padding shadow-none">
                        <div className="table-responsive">
                            <table className="table table-bordered table-striped table-hover mb-0">
                                <thead className="table-light">
                                <tr>
                                    <th className="col-2"><i className="bi bi-clock-history"></i> Status</th>
                                    <th className="col-8"><i className="bi bi-building"></i> Brand Name</th>
                                    {editMode && <th className="col-2"><i className="bi bi-gear"></i> Actions</th>}
                                </tr>
                                </thead>
                                <tbody>
                                {lensList.map(lens => (
                                    <tr key={lens.id}>
                                        <td>
                                            <div className="form-check form-switch">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id={`status-${lens.id}`}
                                                    checked={lens.isActive === true || lens.isActive === 'true'}
                                                    onChange={(e) => handleStatusChange(lens.id, e.target.checked, lens.brandName)}
                                                    disabled={!editMode}
                                                />
                                                <label className="form-check-label" htmlFor={`status-${lens.id}`}>
                                                    {lens.isActive === true || lens.isActive === 'true' ? 'Active' : 'Inactive'}
                                                </label>
                                            </div>
                                        </td>
                                        <td className="fw-semibold">{lens.brandName}</td>
                                        {editMode && (
                                            <td>
                                                <button
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={() => handleDelete(lens.id, lens.brandName)}
                                                >
                                                    <i className="bi bi-trash"></i>
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LensTab;